import "./ChecklistDetail.scss"
import React, { useState, useEffect, useRef, useCallback } from "react"
import DataGrid, { Column, DataGridRef, Editing, Item, Lookup, Toolbar } from "devextreme-react/data-grid"
import ChecklistsApi from "../api/ChecklistApi"
import { useNavigate, useParams } from "react-router-dom"
import { CategorySelector } from "../component/CategorySelector"
import { Checklist, ChecklistQuestion, ChecklistScheduleDto } from "../model/ChecklistDtos"
import { QuestionRequest } from "../api/ChecklistRequests"
import { t } from "i18next"
import { usePopup } from "../../../core/context/PopupContext"
import SchedulesModal from "../component/SchedulesModal"
import { Button } from "devextreme-react"
import ChecklistQuestionsApi from "../api/ChecklistQuestionsApi"
import { toast } from "react-toastify"
import { ActionBar, Card, Collapsible, IconButtonDelete, IconButtonGoBack, Left, LoadIndicator, Right, Row, TextAreaInput, TextInput } from "../../../core/components"
import { useMediaQuery } from "react-responsive"

export default function ChecklistDetail() {
    const params = useParams<{ id: string }>()
    const [name, setName] = useState("")
    const [code, setCode] = useState("")
    const [description, setDescription] = useState("")
    const [questions, setQuestions] = useState<ChecklistQuestion[]>([])
    const [loading, setLoading] = useState(false)
    const [isGridExpanded, setIsGridExpanded] = useState(false)
    const [checklist, setChecklist] = useState<Checklist>()
    const [schedules, setSchedules] = useState<ChecklistScheduleDto[]>([])
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [previousName, setPreviousName] = useState<string>("")
    const [previousCode, setPreviousCode] = useState<string>("")
    const [previousDescription, setPreviousDescription] = useState<string>("")
    const [previousQuestions, setPreviousQuestions] = useState<ChecklistQuestion[]>([])
    const isSmall = useMediaQuery({query: "(max-width: 750px)"})

    const dataGridRef = useRef<DataGridRef>(null)
    const navigate = useNavigate()
    const popup = usePopup()

    const answerTypes = [
        { answerType: 0, name: t("Yes/No") },
        { answerType: 1, name: t("Temperature") },
        { answerType: 2, name: t("Text25") },
    ];

    const fetchChecklist = async (checklistId?: string) => {
        if (!checklistId || loading)
            return

        setLoading(true)
        await ChecklistsApi.getChecklistById(checklistId, true)
            .then((response) => {
                if (response) {
                    setChecklist(response)
                    setName(response.name)
                    setPreviousName(response.name)
                    setCode(response.code)
                    setPreviousCode(response.code)
                    setDescription(response.description)
                    setPreviousDescription(response.description)
                    setSchedules(response.schedules)

                    const processedQuestions = ensureCorrectSequence(response.questions)

                    setQuestions(processedQuestions)
                    setPreviousQuestions(processedQuestions)
                } else {
                    toast.error("Failed to load checklist data")
                }
            }).catch(() => {
                toast.error("Failed to load checklist data")
            }).finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchChecklist(params.id)
    }, [params.id])

    const anyChangesMade = () => {
        return previousName !== name
            || previousDescription !== description
            || previousCode !== code
            || !areQuestionsEqual(previousQuestions, questions)
    }

    const areQuestionsEqual = (prevQuestions: ChecklistQuestion[], currentQuestions: ChecklistQuestion[]) => {
        if (prevQuestions.length !== currentQuestions.length) return false

        for (let i = 0; i < prevQuestions.length; i++) {
            const prevQuestion = prevQuestions[i]
            const currentQuestion = currentQuestions[i]

            if (prevQuestion.sequenceNo !== currentQuestion.sequenceNo
                || prevQuestion.hint !== currentQuestion.hint
                || prevQuestion.description !== currentQuestion.description
                || prevQuestion.answerType !== currentQuestion.answerType
                || (prevQuestion.category?.idChecklistQuestionCategory !== currentQuestion.category?.idChecklistQuestionCategory && prevQuestion.category?.name !== currentQuestion.category?.name)) {
                return false
            }
        }

        return true
    }

    const handleDiscardChanges = () => {
        if (anyChangesMade()) {
            popup.showPopup(
                t("Discard Changes"),
                t("Are you sure you want to discard changes?"),
                () => {
                    navigate("/checklists", { replace: true })
                })
            return
        }

        navigate("/checklists", { replace: true })
    }

    const mapChecklistQuestionToRequest = (question: ChecklistQuestion, idChecklist: number): QuestionRequest => ({
        sequenceNo: question.sequenceNo || 0,
        hint: question.hint || "",
        description: question.description || "",
        answerType: question.answerType || 0,
        category: (question.category?.idChecklistQuestionCategory == null && question.category?.name === "") ? undefined : question.category,
        idChecklist: idChecklist,
        idChecklistQuestion: question.idChecklistQuestion ?? undefined,
    })

    const handleSaveChecklist = async () => {
        setLoading(true)

        if (!name) {
            toast.error("Please fill in the name")
            setLoading(false)
            return
        }

        const dataGrid = dataGridRef.current?.instance()
        if (!dataGrid) {
            toast.error("DataGrid is not initialized")
            setLoading(false)
            return
        }

        const updatedQuestions: ChecklistQuestion[] = dataGrid
            .getDataSource()
            .items()
            .map((item: ChecklistQuestion) => ({ ...item }))

        const checklist = { idChecklist: params.id ?? 0, name, code, description }

        try {
            const result = params.id
                ? await ChecklistsApi.updateChecklist(params.id, checklist)
                : await ChecklistsApi.addChecklist(checklist)

            if (result.succeeded) {
                const checklistId = params.id || result.id

                const questionRequests = updatedQuestions.map((q) =>
                    mapChecklistQuestionToRequest(q, Number(checklistId))
                )

                const upsertResult = await ChecklistQuestionsApi.upsertQuestions(
                    Number(checklistId),
                    questionRequests
                )

                if (upsertResult.success) {
                    setQuestions(ensureCorrectSequence(upsertResult.questions || []))
                    toast.success(`Checklist ${params.id ? "updated" : "added"} successfully`)

                    setPreviousCode(code)
                    setPreviousDescription(description)
                    setPreviousName(name)
                    setPreviousQuestions(ensureCorrectSequence(upsertResult.questions || []))

                    if (!params.id) {
                        navigate(`/checklists/${result.id}`, { replace: true })
                    }
                } else {
                    toast.error(upsertResult.message)
                }
            } else {
                toast.error(`Failed to save checklist: ${result.errorMessage}`)
            }
        } catch (error) {
            toast.error("An unexpected error occurred while saving the checklist")
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteChecklist = async () => {
        if (!params.id) return

        setLoading(true)
        if (await ChecklistsApi.deleteChecklist(Number(params.id))) {
            navigate(-1)
        }
        setLoading(false)
    }

    const ensureCorrectSequence = (questions: ChecklistQuestion[]) => {
        const sortedQuestions = questions.sort((a, b) => a.sequenceNo - b.sequenceNo)
        sortedQuestions.forEach((question, index) => {
            question.sequenceNo = index
        })

        return sortedQuestions
    }

    const onReorder = useCallback((e: any) => {
        let orderedQuestions = reorderQuestions(questions, e.fromIndex, e.toIndex)
        setQuestions(orderedQuestions)
    }, [questions])

    const reorderQuestions = (questions: ChecklistQuestion[], fromIndex: number, toIndex: number): ChecklistQuestion[] => {
        let reorderedQuestions = [...questions]
        const [movedItem] = reorderedQuestions.splice(fromIndex, 1)
        reorderedQuestions.splice(toIndex, 0, movedItem)

        reorderedQuestions.forEach((question, index) => {
            question.sequenceNo = index
        })

        return reorderedQuestions
    }

    const onScheduleClicked = () => {
        setIsModalOpen(true)
    }

    const onScheduleDeleted = () => {
        fetchChecklist(params.id)
    }

    const onScheduleSaved = () => {
        fetchChecklist(params.id)
    }

    const handleDeleteQuestion = (cellInfo: any) => {
        const dataGrid = dataGridRef.current?.instance()
        if (!dataGrid) return

        const rowIndex = cellInfo.rowIndex
        if (rowIndex != null) {
            dataGrid.deleteRow(rowIndex)
        }
    }

    return (
        <React.Fragment>
            <ActionBar>
                <Left>
                    <IconButtonGoBack onClick={handleDiscardChanges} />
                    <Collapsible collapsed={isSmall} closeDropdownOnItemClick>
                        <Button className="border-radius-small" text={t("Save")} stylingMode="contained" type="default" onClick={handleSaveChecklist} disabled={loading} />
                        <Button text={t("Discard")} stylingMode="outlined" onClick={handleDiscardChanges} disabled={loading} />
                        {params.id && <Button className="border-radius-small" text={t("Schedules") + ` (${schedules.length})`} stylingMode="contained" type="default" onClick={onScheduleClicked} disabled={loading} />}
                    </Collapsible>
                </Left>
                <Right>
                    {params.id && <IconButtonDelete onClick={handleDeleteChecklist} />}
                </Right>
            </ActionBar>

            <LoadIndicator loading={loading} />

            <Card>
                <Card margin={1} padding={1} borderRadius={"small"} className="background-secondary">
                    <TextInput
                        className="add-checklist-input"
                        label={t("Name")}
                        disabled={loading}
                        required
                        value={name}
                        onChange={setName} />
                    <TextInput
                        className="add-checklist-input"
                        label={t("Code")}
                        disabled={loading || !!params.id}
                        required
                        value={code}
                        onChange={setCode} />
                    <TextAreaInput
                        className="add-checklist-input"
                        label={t("Description")}
                        disabled={loading}
                        value={description}
                        onChange={setDescription} />
                </Card>

                <DataGrid
                    className={`sticky-header border-radius-medium ${isGridExpanded ? '' : 'grid-height'}`}
                    disabled={loading}
                    ref={dataGridRef}
                    dataSource={questions}
                    showBorders={true}
                    columnAutoWidth={true}
                    rowDragging={{ allowReordering: true, onDragEnd: onReorder }}
                    onInitNewRow={(e) => {
                        e.data.sequenceNo = Math.max(...questions.map(e => e.sequenceNo)) + 1
                    }} >
                    <Editing
                        mode="cell"
                        allowAdding={true}
                        allowUpdating={true}
                        confirmDelete={false} />
                    <Toolbar>
                        <Item
                            widget="dxButton"
                            location="before"
                            cssClass="border-radius-small"
                            options={{
                                icon: "add",
                                text: t("Add Question"),
                                stylingMode: "contained",
                                type: "default",
                                onClick: () => dataGridRef.current?.instance().addRow()
                            }} />
                        <Item
                            widget="dxButton"
                            location="after"
                            options={{
                                icon: `${isGridExpanded ? 'chevronup' : 'chevrondown'}`,
                                text: `${isGridExpanded ? t('Collapse') : t('Expand')}`,
                                onClick: () => setIsGridExpanded(!isGridExpanded)
                            }} />
                    </Toolbar>
                    <Column dataField="idChecklistQuestion" visible={false} />
                    <Column dataField="description" caption={t("Question")} width={'300px'} validationRules={[{ type: 'required', message: t("Question is required") }]} />
                    <Column dataField="hint" caption={t("Hint")} />
                    <Column
                        dataField="answerType"
                        caption={t("Type")}
                        validationRules={[{ type: 'required' }]}
                        minWidth={150}>
                        <Lookup dataSource={answerTypes} valueExpr="answerType" displayExpr="name" />
                    </Column>
                    <Column
                        dataField="category"
                        validationRules={[{ type: "required" }]}
                        caption={t("Category")}
                        dataType="object"
                        cellRender={({ data }) => <span>{data.category?.name}</span>}
                        editCellComponent={(props) => (
                            <CategorySelector {...props} checklistId={params.id} />
                        )} />
                    <Column cellRender={(cellInfo) => (
                        <Row>
                            <Row.End>
                                <IconButtonDelete size="small" onClick={() => handleDeleteQuestion(cellInfo)} />
                            </Row.End>
                        </Row>
                    )} />
                </DataGrid>
            </Card>

            <SchedulesModal
                isOpen={isModalOpen}
                checklist={checklist}
                onClose={() => setIsModalOpen(false)}
                onDeleteSchedule={onScheduleDeleted}
                onSaveSchedule={onScheduleSaved}
                schedules={schedules} />

        </React.Fragment>
    )
}
